import React, { useEffect, useState } from "react";
import { IconButton, Modal, TableRow } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import {
    ButtonRow,
    Expandable,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
    TitleWrapper,
} from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../../MaterialOrder/Styled";
import CustomTable from "../../../../shared/components/CustomTable";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import { api } from "../../../../shared/hooks/api";
import { error, success } from "../../../../utils/toast";
import { ImportProductForm } from "./ImportProductForm";
import useMergeState from "../../../../shared/hooks/useMergeState";
import LoadingButton from "../../../../shared/components/LoadingButton";
import DocxPreviewModal from "../../../../shared/components/DocxPreviewModal";
import { generateDocumentBlob } from "../../../../utils/exportUtils";
import { buildDocxData } from "../../../../utils/docxBuilder";
import { productImportPreviewDescriptor } from "../../../../utils/docxMappings";
import ImportTemplate from "../../../../templates/phieu_nhap_kho_san_pham.docx";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { buildInventoryModalSearchParams } from "../../../../utils/inventoryModalSearchParams";

const ImportProductTableColumns = [
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Số lượng nhập", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "", icon: <></> },
];

interface ProductRowProps {
    item: any;
    extraRowProps: {
        onChangeImportQuantity: (id: string, quantity: number) => void;
        onChangeNote: (id: string, note: string) => void;
        removeSelectedItem: (item: any) => void;
    };
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const {
        onChangeImportQuantity,
        onChangeNote,
        removeSelectedItem,
    } = extraRowProps;

    return (
        <TableRow>
            <StyledTableCell>{item.name}</StyledTableCell>
            <StyledTableCell>
                <TextBox
                    numeric
                    style={{ width: "80px" }}
                    text={formatInternationalNumber(item.importQuantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        onChangeImportQuantity(item.id, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <TextBox
                    text={item.note || ""}
                    onChangeText={(value: string) => onChangeNote(item.id, value)}
                    style={{ width: "150px" }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <IconButton onClick={() => removeSelectedItem(item)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

const initialData = {
    receiver_name: "",
    customer: null,
    import_method: "",
    note: "",
    products: [],
    import_date: new Date(),
};

type PrefillItem = {
    id: string;
    name: string;
    importQuantity: number;
    note?: string;
};

interface ImportProductModalProps {
    open: boolean;
    handleClose: () => void;
    onImport?: () => void;
    initialFormData?: Partial<typeof initialData>;
    initialItems?: PrefillItem[];
    onAfterImport?: () => void;
}

export const ImportProductModal = ({
                                       open,
                                       handleClose,
                                       onImport,
                                       initialFormData,
                                       initialItems,
                                       onAfterImport,
                                   }: ImportProductModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [tableItems, setTableItems] = useState<any[]>([]);
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [formData, mergeFormData] = useMergeState(initialData);
    const [{ data }, fetchProducts] = api.get(
        "/products/",
        buildInventoryModalSearchParams(),
        true,
    );

    const [{ isCreating }, createImport] = api.post("/products/import/");

    const fetchItems = (variables: any) => {
        fetchProducts(buildInventoryModalSearchParams(variables), true);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
            if (initialFormData) {
                mergeFormData({ ...initialData, ...initialFormData });
            } else {
                mergeFormData({ ...initialData });
            }
            if (initialItems && Array.isArray(initialItems) && initialItems.length > 0) {
                setTableItems(initialItems);
                setSelectedItems(initialItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setIsPreviewOpen(false);
        setPreviewBlob(null);
        setTimeout(handleClose, 200);
    };

    const handleOnclickItem = (item: any, checked: boolean) => {
        if (checked) {
            if (!tableItems.find((product) => product.id === item.id)) {
                setTableItems([
                    ...tableItems,
                    {
                        ...item,
                        importQuantity: 0,
                        import_date: new Date().toISOString().split("T")[0],
                        note: "",
                    },
                ]);
                setSelectedItems([...selectedItems, item]);
            }
        } else {
            const updatedItems = tableItems.filter(
                (product) => product.id !== item.id,
            );
            const updatedSelectedItems = selectedItems.filter(
                (product) => product.id !== item.id,
            );
            setTableItems(updatedItems);
            setSelectedItems(updatedSelectedItems);
        }
    };

    const onChangeImportQuantity = (id: string, quantity: number) => {
        const updatedProducts = tableItems.map((product) => {
            if (product.id === id)
                return { ...product, importQuantity: quantity };
            return product;
        });
        setTableItems(updatedProducts);
    };

    const onChangeNote = (id: string, note: string) => {
        const updatedProducts = tableItems.map((product) => {
            if (product.id === id) return { ...product, note };
            return product;
        });
        setTableItems(updatedProducts);
    };

    const removeSelectedItem = (removedItem: any) => {
        const updatedItems = tableItems.filter(
            (item) => item.id !== removedItem.id,
        );
        const updatedSelectedItems = selectedItems.filter(
            (item) => item.id !== removedItem.id,
        );
        setTableItems(updatedItems);
        setSelectedItems(updatedSelectedItems);
    };

    const onClickImport = async () => {
        const validation = validateImportData();
        if (!validation.isValid) {
            alert(validation.errors.join("\n\n"));
            return;
        }

        const formattedProducts = tableItems.map((item) => ({
            product: item.id,
            quantity: item.importQuantity,
            note: item.note || "",
        }));

        const importData = {
            ...formData,
            products: formattedProducts,
            customer: formData.customer.id,
        };

        try {
            await createImport(importData);
            if (onAfterImport) {
                try {
                    await onAfterImport();
                } catch (headerErr) {
                    console.error("Failed to update delivery trip header:", headerErr);
                    error("Cảnh báo", "Nhập kho thành công nhưng không thể cập nhật trạng thái chuyến giao hàng");
                }
            }
            onImport && onImport();
            success("Nhập kho sản phẩm thành công", "Nhập kho sản phẩm thành công");
            setIsPreviewOpen(false);
            setPreviewBlob(null);
            handleCloseWithAnimation();
        } catch (err) {
            error("Nhập kho sản phẩm thất bại", err);
        }
    };

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    const handlePreview = async () => {
        const validation = validateImportData();
        if (!validation.isValid) {
            alert("Vui lòng điền đầy đủ thông tin trước khi xem trước:\n\n" + validation.errors.join("\n\n"));
            return;
        }
        try {
            const data = buildDocxData(
                { formData, tableItems },
                productImportPreviewDescriptor,
            );
            const blob = await generateDocumentBlob(data, ImportTemplate);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate product import preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const validateImportData = (): ValidationResult => {
        const errors: string[] = [];

        // Validate customer
        if (!formData.customer) {
            errors.push("Vui lòng chọn khách hàng");
        }

        // Validate import method
        if (!formData.import_method) {
            errors.push("Vui lòng chọn phương thức nhập");
        }

        // Validate products
        if (tableItems.length === 0) {
            errors.push("Vui lòng chọn ít nhất một sản phẩm");
        }

        // Check if any products have zero or negative quantity
        const invalidQuantityProducts = tableItems.filter(item =>
            !item.importQuantity || item.importQuantity <= 0,
        );

        if (invalidQuantityProducts.length > 0) {
            errors.push("Vui lòng nhập số lượng lớn hơn 0 cho tất cả sản phẩm");
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    return (
        <>
            <Modal
                open={open}
                onClose={(_, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                    handleCloseWithAnimation();
                }}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        <TitleWrapper>
                            <TitleText>Nhập kho sản phẩm</TitleText>
                        </TitleWrapper>

                        <ImportProductForm formData={formData} onChange={mergeFormData} />

                        <TitleText>Danh sách nhập kho sản phẩm</TitleText>

                        <SearchBarWithSuggestion
                            menuList={data ? data.results : []}
                            selectedItems={selectedItems}
                            fetchItems={fetchItems}
                            handleOnclickItem={handleOnclickItem}
                        />

                        <CustomTable
                            itemList={tableItems}
                            RowElement={ProductRow}
                            TableColumns={ImportProductTableColumns}
                            extraRowProps={{
                                onChangeImportQuantity,
                                onChangeNote,
                                removeSelectedItem,
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">Xem trước
                            phiếu</StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu nhập kho thành phẩm"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={onClickImport}
                        disabled={isCreating}
                        isLoading={isCreating}
                        text="Nhập kho"
                    />
                }
            />
        </>
    );
};
